import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { StrollerReviewPhoto } from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto";
import { strollerReviewPhotoFetchTrigger } from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto.actions";
import { strollersReviewsPhotosReducer } from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto.reducers";
import strollersReviewsPhotosSaga from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto.sagas";
import { getStrollerReviewPhotoState } from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto.selectors";
import { strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewPhotoSchema,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    reviewId: number;
    photoId: number;
    loadingState: LoadingState;
    errorMessage: string;
    stroller: StrollerEntity;
    strollerReview: StrollerUserReviewEntity;
    strollerReviewPhotos: StrollerUserReviewPhotoEntity[];
    strollerReviewPhoto: StrollerUserReviewPhotoEntity;
}

export interface DispatchProps {
    onLoad(slug: string, reviewId: number, photoId: number);
}

export type RouteProps = PageRouteProps<{
    slug: string;
    reviewId: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerReviewPhotoPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.routeParams.slug;
            const reviewId = parseInt(ownProps.routeParams.reviewId, 10);
            const photoId = parseInt(ownProps.routeParams.photoId, 10);

            const photoState = getStrollerReviewPhotoState(state, slug);
            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                photoState.strollerId,
            );
            const strollerReview = getDenormalizedEntity<StrollerUserReviewEntity>(
                state,
                StrollerUserReviewSchema,
                photoState.strollerReviewId,
            );
            const strollerReviewPhotos = getDenormalizedEntities<StrollerUserReviewPhotoEntity>(
                state,
                StrollerUserReviewPhotoSchema,
                photoState.strollerReviewPhotoIds,
            );
            const strollerReviewPhoto = strollerReviewPhotos.find(
                (p) => p.photoFile.id === photoId,
            );

            const loadingState =
                photoState.loadingState != LoadingState.SUCCESS
                    ? photoState.loadingState
                    : strollerReviewPhoto
                      ? LoadingState.SUCCESS
                      : LoadingState.FAILURE;
            const errorMessage =
                photoState.loadingState != LoadingState.SUCCESS
                    ? photoState.errorMessage
                    : strollerReviewPhoto
                      ? null
                      : "NOT FOUND";

            return {
                strollerSlug: slug,
                reviewId,
                photoId,
                loadingState,
                errorMessage,
                stroller,
                strollerReview,
                strollerReviewPhotos,
                strollerReviewPhoto,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerReviewPhotoFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerReviewPhoto {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug, reviewId } = props;
        return url(strollersReviewsUrl, { slug: strollerSlug }, {}, `review-${reviewId}`);
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.strollers.strollersReviewsPhotos",
            strollersReviewsPhotosReducer,
        );
        store.injectSaga("containers.strollers.strollersReviewsPhotos", strollersReviewsPhotosSaga);
    }
}
